import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { MapPin, Plus, Edit, Trash2, LogOut, Home, Upload, X, Car, Clock, CheckCircle, XCircle, History, TrendingUp } from 'lucide-react';
import AnalyticsDashboard from '@/components/AnalyticsDashboard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { storage } from '@/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const API = '/api';
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB por foto
const MAX_TOTAL_SIZE = 5 * 1024 * 1024; // 5MB total por estacionamento
const MAX_PHOTOS = 3; // Máximo de 3 fotos por estacionamento

// Default pricing configuration
const defaultPricingConfig = {
  hourly_enabled: true,
  hourly_rate: 0,
  progressive_enabled: false,
  first_hour_rate: 0,
  additional_hour_rate: 0,
  period_enabled: false,
  period_rate: 0,
  daily_enabled: false,
  daily_rate: 0,
  morning_shift_enabled: false,
  morning_shift_rate: 0,
  afternoon_shift_enabled: false,
  afternoon_shift_rate: 0,
  night_shift_enabled: false,
  night_shift_rate: 0
};

const OwnerDashboard = () => {
  const { currentUser, authToken, signOut } = useAuth();
  const navigate = useNavigate();
  const [parkingLots, setParkingLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLot, setEditingLot] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]); // Array de arquivos
  const [photoPreviews, setPhotoPreviews] = useState([]); // Array de previews
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('parkings'); // 'parkings', 'vehicles', 'pricing', 'finances', or 'mercadopago'
  const [selectedLotForPricing, setSelectedLotForPricing] = useState(null);
  const [pricingConfig, setPricingConfig] = useState(defaultPricingConfig);
  const [savingPricing, setSavingPricing] = useState(false);
  const [mpStatus, setMpStatus] = useState({ connected: false, loading: true });
  const [mpAuthUrl, setMpAuthUrl] = useState(null);
  const [authUrlError, setAuthUrlError] = useState(false);
  const [earnings, setEarnings] = useState(null);
  // Vehicle management states
  const [activeVehicles, setActiveVehicles] = useState([]);
  const [vehicleHistory, setVehicleHistory] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [historyDate, setHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  const [bankAccount, setBankAccount] = useState({
    bank_name: '',
    account_type: 'checking',
    agency: '',
    account_number: '',
    account_holder_name: '',
    cpf_cnpj: '',
    pix_key: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: '',  // Descrição curta (máx 150 caracteres)
    latitude: -23.5505,
    longitude: -46.6333,
    total_spots: 0,
    available_spots: 0,
    price_per_hour: 0,
    parking_type: 'indoor',
    schedule: '24 horas',
    photo_url: null,  // Mantido para compatibilidade
    photo_urls: []    // Novo: array de URLs
  });

  // useEffect moved to after function definitions

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (response.data.bank_account) {
        setBankAccount(response.data.bank_account);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API}/owner/earnings`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setEarnings(response.data);
    } catch (error) {
      console.error('Error fetching earnings:', error);
    }
  };

  const fetchMpStatus = async () => {
    try {
      const response = await axios.get(`${API}/owner/mp-status`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setMpStatus({ ...response.data, loading: false });
    } catch (error) {
      console.error('Error fetching MP status:', error);
      setMpStatus({ connected: false, loading: false });
    }
  };

  async function fetchMpAuthUrl() {
    if (!authToken) return;
    setAuthUrlError(false);
    try {
      const response = await axios.get(`${API}/owner/mp-connect`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setMpAuthUrl(response.data.authorization_url);
    } catch (error) {
      console.error('Error fetching MP Auth URL:', error);
      setAuthUrlError(true);
    }
  }

  // ===== VEHICLE MANAGEMENT FUNCTIONS =====
  
  const fetchActiveVehicles = async () => {
    setVehiclesLoading(true);
    try {
      const response = await axios.get(`${API}/owner/vehicles/active`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setActiveVehicles(response.data);
    } catch (error) {
      console.error('Error fetching active vehicles:', error);
      toast.error('Erro ao carregar veículos');
    } finally {
      setVehiclesLoading(false);
    }
  };

  const fetchVehicleHistory = async (date) => {
    setVehiclesLoading(true);
    try {
      const response = await axios.get(`${API}/owner/vehicles/history`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { date }
      });
      setVehicleHistory(response.data);
    } catch (error) {
      console.error('Error fetching vehicle history:', error);
    } finally {
      setVehiclesLoading(false);
    }
  };

  const cancelVehicleEntry = async (checkInId) => {
    if (!window.confirm('Cancelar entrada deste veículo? A vaga será liberada sem pagamento.')) {
      return;
    }
    try {
      await axios.post(`${API}/owner/vehicles/cancel/${checkInId}`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success('Entrada cancelada. Vaga liberada.');
      fetchActiveVehicles();
      fetchMyParkingLots();
    } catch (error) {
      console.error('Error cancelling entry:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cancelar entrada');
    }
  };

  const markVehiclePaid = async (checkInId) => {
    if (!window.confirm('Marcar como pago presencialmente? A vaga será liberada.')) {
      return;
    }
    try {
      const response = await axios.post(`${API}/owner/vehicles/mark-paid/${checkInId}`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success(`Pago! R$ ${response.data.total_amount.toFixed(2)} - ${response.data.hours_charged}h`);
      fetchActiveVehicles();
      fetchVehicleHistory(historyDate);
      fetchMyParkingLots();
    } catch (error) {
      console.error('Error marking paid:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registrar pagamento');
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  };

  const disconnectMercadoPago = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar sua conta Mercado Pago?')) {
      return;
    }
    try {
      await axios.post(`${API}/owner/mp-disconnect`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success('Conta Mercado Pago desconectada');
      fetchMpStatus();
    } catch (error) {
      console.error('Error disconnecting MP:', error);
      toast.error('Erro ao desconectar');
    }
  };

  const saveBankAccount = async () => {
    try {
      await axios.put(`${API}/profile/bank-account`, bankAccount, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success('Dados bancários salvos com sucesso!');
    } catch (error) {
      console.error('Error saving bank account:', error);
      toast.error('Erro ao salvar dados bancários');
    }
  };

  const savePricingConfig = async () => {
    if (!selectedLotForPricing) {
      toast.error('Selecione um estacionamento');
      return;
    }
    
    setSavingPricing(true);
    try {
      // Atualizar também o price_per_hour principal com o valor da hora
      const updateData = {
        pricing_config: pricingConfig,
        price_per_hour: pricingConfig.hourly_rate || selectedLotForPricing.price_per_hour
      };
      
      await axios.put(`${API}/parking-lots/${selectedLotForPricing.id}`, updateData, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      toast.success('Configuração de preços salva com sucesso!');
      fetchMyParkingLots();
    } catch (error) {
      console.error('Error saving pricing config:', error);
      toast.error('Erro ao salvar configuração de preços');
    } finally {
      setSavingPricing(false);
    }
  };

  const handleSelectLotForPricing = (lot) => {
    setSelectedLotForPricing(lot);
    // Carregar configuração existente ou usar padrões
    if (lot.pricing_config) {
      setPricingConfig({
        ...defaultPricingConfig,
        ...lot.pricing_config,
        hourly_rate: lot.pricing_config.hourly_rate || lot.price_per_hour
      });
    } else {
      setPricingConfig({
        ...defaultPricingConfig,
        hourly_rate: lot.price_per_hour
      });
    }
  };

  const fetchMyParkingLots = async () => {
    try {
      const response = await axios.get(`${API}/my-parking-lots`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setParkingLots(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar estacionamentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    fetchMyParkingLots();
    fetchEarnings();
    fetchProfile();
    fetchMpStatus();
    fetchMpAuthUrl();

    // Check if returning from MP OAuth
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('mp_connected') === 'true') {
      toast.success('Conta Mercado Pago conectada com sucesso!');
      setActiveTab('mercadopago');
      window.history.replaceState({}, '', '/dashboard');
    } else if (urlParams.get('mp_error') === 'true') {
      toast.error('Erro ao conectar Mercado Pago. Tente novamente.');
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [currentUser, navigate, authToken]);

  // Fetch vehicles when tab changes
  useEffect(() => {
    if (activeTab === 'vehicles' && authToken) {
      fetchActiveVehicles();
      fetchVehicleHistory(historyDate);
      
      // Auto-refresh every 30 seconds when on vehicles tab
      const interval = setInterval(() => {
        fetchActiveVehicles();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [activeTab, authToken, historyDate]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Verificar quantidade máxima
    const totalPhotos = photoPreviews.length + files.length;
    if (totalPhotos > MAX_PHOTOS) {
      toast.error(`Máximo de ${MAX_PHOTOS} fotos por estacionamento!`);
      return;
    }

    // Calcular tamanho total atual
    const currentTotalSize = selectedFiles.reduce((acc, f) => acc + f.size, 0);
    
    const validFiles = [];
    const newPreviews = [];

    for (const file of files) {
      // Validar tamanho individual
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`Foto "${file.name}" muito grande! Máximo 2 MB por foto.`);
        continue;
      }

      // Validar tamanho total
      const newTotalSize = currentTotalSize + validFiles.reduce((acc, f) => acc + f.size, 0) + file.size;
      if (newTotalSize > MAX_TOTAL_SIZE) {
        toast.error('Limite total de 5 MB excedido!');
        break;
      }

      // Validar formato
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error(`Formato inválido para "${file.name}"! Use JPEG, PNG ou WebP.`);
        continue;
      }

      validFiles.push(file);
      
      // Gerar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removePhoto = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingPhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photo_urls: prev.photo_urls.filter((_, i) => i !== index)
    }));
  };

  const uploadPhotos = async () => {
    if (!selectedFiles.length) return [];

    setUploading(true);
    const uploadedUrls = [];
    
    try {
      // Verificar se storage está disponível
      if (!storage) {
        toast.error('Firebase Storage não está configurado. Salve sem fotos por enquanto.');
        return [];
      }

      // Verificar se usuário está autenticado
      if (!currentUser || !currentUser.uid) {
        toast.error('Você precisa estar logado para enviar fotos.');
        return [];
      }

      console.log('📸 Iniciando upload de', selectedFiles.length, 'foto(s)...');
      console.log('👤 Usuário:', currentUser.uid);

      for (const file of selectedFiles) {
        try {
          console.log(`📤 Enviando: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
          
          // Criar referência única no Firebase Storage
          const fileName = `${currentUser.uid}/${Date.now()}_${file.name}`;
          const storageRef = ref(storage, `parking-lots/${fileName}`);
          
          // Upload com timeout de 60 segundos (aumentado)
          const uploadPromise = uploadBytes(storageRef, file);
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout - upload demorou mais de 60 segundos')), 60000)
          );
          
          await Promise.race([uploadPromise, timeoutPromise]);
          console.log(`✅ Upload concluído: ${file.name}`);
          
          // Obter URL pública
          const downloadURL = await getDownloadURL(storageRef);
          console.log(`🔗 URL obtida: ${downloadURL.substring(0, 50)}...`);
          uploadedUrls.push(downloadURL);
        } catch (fileError) {
          console.error(`❌ Erro ao enviar ${file.name}:`, fileError);
          
          // Mensagem mais específica baseada no erro
          if (fileError.code === 'storage/unauthorized') {
            toast.error('Sem permissão para upload. Verifique as regras do Firebase Storage.');
          } else if (fileError.code === 'storage/canceled') {
            toast.error('Upload cancelado.');
          } else if (fileError.message?.includes('Timeout')) {
            toast.error(`Timeout ao enviar ${file.name}. Tente uma foto menor.`);
          } else {
            toast.error(`Erro ao enviar ${file.name}: ${fileError.message || 'erro desconhecido'}`);
          }
        }
      }
      
      if (uploadedUrls.length > 0) {
        toast.success(`${uploadedUrls.length} foto(s) enviada(s) com sucesso!`);
      } else if (selectedFiles.length > 0) {
        toast.error('Nenhuma foto foi enviada. Verifique o console para mais detalhes.');
      }
      return uploadedUrls;
    } catch (error) {
      console.error('❌ Upload error:', error);
      toast.error('Erro ao enviar fotos: ' + (error.message || 'erro desconhecido'));
      return uploadedUrls;
    } finally {
      setUploading(false);
    }
  };

  // Manter função antiga para compatibilidade
  const uploadPhoto = async () => {
    if (!selectedFiles.length) return null;

    setUploading(true);
    try {
      const file = selectedFiles[0];
      const fileName = `${currentUser.uid}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `parking-lots/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      toast.success('Foto enviada com sucesso!');
      return downloadURL;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erro ao enviar foto');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Combinar fotos existentes com novas
      let allPhotoUrls = [...(formData.photo_urls || [])];
      
      // Upload novas fotos se selecionadas
      if (selectedFiles.length > 0) {
        toast.info('Enviando fotos...');
        const newUrls = await uploadPhotos();
        allPhotoUrls = [...allPhotoUrls, ...newUrls];
        
        // Se tinha arquivos mas nenhum foi enviado, avisar mas continuar
        if (newUrls.length === 0 && selectedFiles.length > 0) {
          toast.warning('Fotos não puderam ser enviadas. Salvando sem as novas fotos.');
        }
      }

      // Limitar a 3 fotos
      allPhotoUrls = allPhotoUrls.slice(0, MAX_PHOTOS);

      const dataToSend = { 
        ...formData, 
        photo_urls: allPhotoUrls,
        photo_url: allPhotoUrls[0] || null // Manter compatibilidade
      };

      if (editingLot) {
        await axios.put(`${API}/parking-lots/${editingLot.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        toast.success('Estacionamento atualizado!');
      } else {
        await axios.post(`${API}/parking-lots`, dataToSend, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        toast.success('Estacionamento criado!');
      }
      setShowModal(false);
      setEditingLot(null);
      fetchMyParkingLots();
      resetForm();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erro ao salvar estacionamento');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSpots = async (lotId, newSpots) => {
    try {
      await axios.patch(`${API}/parking-lots/${lotId}/spots?available_spots=${newSpots}`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success('Vagas atualizadas!');
      fetchMyParkingLots();
    } catch (error) {
      toast.error('Erro ao atualizar vagas');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      description: '',
      latitude: -23.5505,
      longitude: -46.6333,
      total_spots: 0,
      available_spots: 0,
      price_per_hour: 0,
      parking_type: 'indoor',
      schedule: '24 horas',
      photo_url: null,
      photo_urls: []
    });
    setSelectedFiles([]);
    setPhotoPreviews([]);
  };

  const handleEdit = (lot) => {
    setEditingLot(lot);
    // Compatibilidade: converter photo_url antigo para photo_urls
    const existingPhotos = lot.photo_urls || (lot.photo_url ? [lot.photo_url] : []);
    setFormData({
      name: lot.name,
      address: lot.address,
      description: lot.description || '',
      latitude: lot.latitude,
      longitude: lot.longitude,
      total_spots: lot.total_spots,
      available_spots: lot.available_spots,
      price_per_hour: lot.price_per_hour,
      parking_type: lot.parking_type,
      schedule: lot.schedule,
      photo_url: lot.photo_url,
      photo_urls: existingPhotos
    });
    if (existingPhotos.length > 0) {
      setPhotoPreviews(existingPhotos);
    }
    setShowModal(true);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Carregando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <MapPin className="w-8 h-8" style={{ color: '#302681' }} />
              <h1 className="text-2xl font-bold" style={{ color: '#302681' }}>Dashboard do Proprietário</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate('/')} variant="outline" data-testid="home-button">
                <Home className="w-4 h-4 mr-2" />
                Início
              </Button>
              <Button onClick={handleLogout} variant="outline" className="text-red-600" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Payment System Status Alert */}
        <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-800">
                <strong>🎉 Sistema de Pagamentos em PRODUÇÃO!</strong> Pagamentos reais via PIX/Mercado Pago ativos. 
                Configure seus preços e comece a receber pagamentos dos clientes através de QR Code PIX!
              </p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('parkings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === 'parkings'
                  ? 'border-[#302681] text-[#302681]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Meus Estacionamentos
            </button>
            <button
              onClick={() => setActiveTab('vehicles')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex items-center space-x-1 ${
                activeTab === 'vehicles'
                  ? 'border-[#009440] text-[#009440]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Car className="w-4 h-4" />
              <span>Veículos Estacionados</span>
              {activeVehicles.length > 0 && (
                <span className="ml-1 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {activeVehicles.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('pricing')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === 'pricing'
                  ? 'border-[#302681] text-[#302681]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ⚙️ Configuração do Estacionamento
            </button>
            <button
              onClick={() => setActiveTab('finances')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === 'finances'
                  ? 'border-[#302681] text-[#302681]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Finanças & Pagamentos
            </button>
            <button
              onClick={() => setActiveTab('mercadopago')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === 'mercadopago'
                  ? 'border-[#302681] text-[#302681]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              💰 Mercado Pago
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex items-center space-x-1 ${
                activeTab === 'analytics'
                  ? 'border-[#302681] text-[#302681]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="analytics-tab-btn"
            >
              <TrendingUp className="w-4 h-4" />
              <span>Analytics</span>
            </button>
          </nav>
        </div>

        {/* Parkings Tab Content */}
        {activeTab === 'parkings' && (
          <>
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Meus Estacionamentos</h2>
          <Button
            onClick={() => {
              resetForm();
              setEditingLot(null);
              setShowModal(true);
            }}
            style={{ backgroundColor: '#302681' }}
            className="text-white"
            data-testid="add-parking-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Estacionamento
          </Button>
        </div>

        {parkingLots.length === 0 ? (
          <Card className="p-12 text-center">
            <MapPin className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold mb-2">Nenhum estacionamento cadastrado</h3>
            <p className="text-gray-600 mb-4">Comece adicionando seu primeiro estacionamento</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {parkingLots.map((lot) => {
              // Compatibilidade: usar photo_urls ou fallback para photo_url
              const photos = lot.photo_urls?.length > 0 ? lot.photo_urls : (lot.photo_url ? [lot.photo_url] : []);
              
              return (
              <Card key={lot.id} className="p-6" data-testid={`parking-card-${lot.id}`}>
                {/* Galeria de Fotos */}
                {photos.length > 0 && (
                  <div className="relative mb-4">
                    {photos.length === 1 ? (
                      <img 
                        src={photos[0]} 
                        alt={lot.name}
                        className="w-full h-40 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="grid grid-cols-3 gap-1 h-40">
                        {photos.slice(0, 3).map((photo, idx) => (
                          <img 
                            key={idx}
                            src={photo} 
                            alt={`${lot.name} - Foto ${idx + 1}`}
                            className={`w-full h-full object-cover ${
                              idx === 0 ? 'rounded-l-lg' : idx === 2 ? 'rounded-r-lg' : ''
                            }`}
                          />
                        ))}
                      </div>
                    )}
                    {photos.length > 1 && (
                      <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                        {photos.length} fotos
                      </span>
                    )}
                  </div>
                )}
                
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold">{lot.name}</h3>
                  <span
                    className="px-3 py-1 rounded-full text-xs text-white"
                    style={{
                      backgroundColor: lot.parking_type === 'indoor' ? '#302681' :
                        lot.parking_type === 'outdoor' ? '#009440' : '#FFCB00'
                    }}
                  >
                    {lot.parking_type === 'indoor' ? 'Coberto' :
                     lot.parking_type === 'outdoor' ? 'Descoberto' : 'Misto'}
                  </span>
                </div>
                
                <p className="text-sm text-gray-600 mb-4">{lot.address}</p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span>Vagas Disponíveis:</span>
                    <span className="font-semibold">{lot.available_spots}/{lot.total_spots}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Preço/hora:</span>
                    <span className="font-semibold">R$ {lot.price_per_hour.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Horário:</span>
                    <span className="font-semibold">{lot.schedule}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="text-sm font-medium mb-2 block">Atualizar Vagas Disponíveis</label>
                  <Input
                    type="number"
                    min="0"
                    max={lot.total_spots}
                    defaultValue={lot.available_spots}
                    onBlur={(e) => {
                      const newValue = parseInt(e.target.value);
                      if (newValue !== lot.available_spots && newValue >= 0 && newValue <= lot.total_spots) {
                        handleUpdateSpots(lot.id, newValue);
                      }
                    }}
                    data-testid={`spots-input-${lot.id}`}
                  />
                </div>

                <div className="flex space-x-2">
                  <Button
                    onClick={() => handleEdit(lot)}
                    variant="outline"
                    className="flex-1"
                    data-testid={`edit-button-${lot.id}`}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                </div>
              </Card>
              );
            })}
          </div>
        )}
          </>
        )}

        {/* Vehicles Tab Content */}
        {activeTab === 'vehicles' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <Car className="w-6 h-6 text-green-600" />
                <span>Veículos Estacionados</span>
              </h2>
              <Button
                onClick={fetchActiveVehicles}
                variant="outline"
                disabled={vehiclesLoading}
              >
                🔄 Atualizar
              </Button>
            </div>

            {/* Active Vehicles */}
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center space-x-2">
                <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                <span>Veículos Atualmente Estacionados ({activeVehicles.length})</span>
              </h3>

              {vehiclesLoading ? (
                <div className="text-center py-8 text-gray-500">
                  Carregando...
                </div>
              ) : activeVehicles.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Car className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Nenhum veículo estacionado no momento</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4 font-semibold">Placa</th>
                        <th className="text-left py-3 px-4 font-semibold">Cor</th>
                        <th className="text-left py-3 px-4 font-semibold">Veículo</th>
                        <th className="text-left py-3 px-4 font-semibold">Nome</th>
                        <th className="text-left py-3 px-4 font-semibold">Entrada</th>
                        <th className="text-left py-3 px-4 font-semibold">Tempo</th>
                        <th className="text-right py-3 px-4 font-semibold">Valor Atual</th>
                        <th className="text-center py-3 px-4 font-semibold">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeVehicles.map((vehicle) => (
                        <tr key={vehicle.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <span className="font-mono font-bold text-lg bg-yellow-100 px-2 py-1 rounded">
                              {vehicle.license_plate}
                            </span>
                          </td>
                          <td className="py-3 px-4">{vehicle.vehicle_color || '-'}</td>
                          <td className="py-3 px-4">{vehicle.vehicle_model || '-'}</td>
                          <td className="py-3 px-4">{vehicle.driver_name || '-'}</td>
                          <td className="py-3 px-4 text-sm">
                            {new Date(vehicle.check_in_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="py-3 px-4">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                              {formatDuration(vehicle.duration_minutes)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className="font-bold text-green-700 text-lg">
                              R$ {vehicle.current_value.toFixed(2)}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex space-x-2 justify-center">
                              <Button
                                onClick={() => markVehiclePaid(vehicle.id)}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                title="Marcar como Pago"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Pago
                              </Button>
                              <Button
                                onClick={() => cancelVehicleEntry(vehicle.id)}
                                size="sm"
                                variant="destructive"
                                title="Cancelar Entrada"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Cancelar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Vehicle History */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg flex items-center space-x-2">
                  <History className="w-5 h-5 text-gray-500" />
                  <span>Histórico de Veículos</span>
                </h3>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">Data:</label>
                  <Input
                    type="date"
                    value={historyDate}
                    onChange={(e) => {
                      setHistoryDate(e.target.value);
                      fetchVehicleHistory(e.target.value);
                    }}
                    className="w-40"
                  />
                </div>
              </div>

              {vehicleHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Nenhum registro para esta data</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4">Placa</th>
                        <th className="text-left py-3 px-4">Veículo</th>
                        <th className="text-left py-3 px-4">Entrada</th>
                        <th className="text-left py-3 px-4">Saída</th>
                        <th className="text-right py-3 px-4">Valor</th>
                        <th className="text-center py-3 px-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehicleHistory.map((vehicle) => (
                        <tr key={vehicle.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <span className="font-mono font-bold">{vehicle.license_plate}</span>
                          </td>
                          <td className="py-3 px-4">
                            {vehicle.vehicle_model || '-'} {vehicle.vehicle_color && `(${vehicle.vehicle_color})`}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {new Date(vehicle.check_in_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {vehicle.check_out_time ? new Date(vehicle.check_out_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                          </td>
                          <td className="py-3 px-4 text-right font-semibold">
                            {vehicle.total_amount ? `R$ ${vehicle.total_amount.toFixed(2)}` : '-'}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              vehicle.status === 'paid' 
                                ? vehicle.paid_in_person 
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {vehicle.status === 'paid' 
                                ? vehicle.paid_in_person 
                                  ? '💵 Presencial'
                                  : '✅ App' 
                                : '❌ Cancelado'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Summary */}
              {vehicleHistory.length > 0 && (
                <div className="mt-4 pt-4 border-t flex justify-between items-center">
                  <span className="text-gray-600">
                    Total de veículos: <strong>{vehicleHistory.length}</strong>
                  </span>
                  <span className="text-lg font-bold text-green-700">
                    Total: R$ {vehicleHistory.reduce((sum, v) => sum + (v.total_amount || 0), 0).toFixed(2)}
                  </span>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Pricing Configuration Tab Content */}
        {activeTab === 'pricing' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">⚙️ Configuração do Estacionamento</h2>
            
            {parkingLots.length === 0 ? (
              <Card className="p-12 text-center">
                <MapPin className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-xl font-semibold mb-2">Nenhum estacionamento cadastrado</h3>
                <p className="text-gray-600 mb-4">Cadastre um estacionamento primeiro na aba &quot;Meus Estacionamentos&quot;</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Seletor de Estacionamento */}
                <div className="lg:col-span-1">
                  <Card className="p-4">
                    <h3 className="font-semibold mb-4">Selecione o Estacionamento</h3>
                    <div className="space-y-2">
                      {parkingLots.map((lot) => (
                        <button
                          key={lot.id}
                          onClick={() => handleSelectLotForPricing(lot)}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                            selectedLotForPricing?.id === lot.id
                              ? 'border-[#302681] bg-[#302681]/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <p className="font-medium">{lot.name}</p>
                          <p className="text-sm text-gray-500">{lot.address}</p>
                        </button>
                      ))}
                    </div>
                  </Card>
                </div>

                {/* Formulário de Configuração de Preços */}
                <div className="lg:col-span-2">
                  {selectedLotForPricing ? (
                    <Card className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold">Configuração de Preços</h3>
                        <span className="text-sm text-gray-500">{selectedLotForPricing.name}</span>
                      </div>

                      {/* Seção: Preço por Hora */}
                      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">Preço por Hora</h4>
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">ATUAL</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4 mb-4">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={pricingConfig.hourly_enabled}
                              onChange={(e) => setPricingConfig({...pricingConfig, hourly_enabled: e.target.checked})}
                              className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                            />
                            <span className="ml-2 text-sm font-medium">Cobrança por hora</span>
                          </label>
                        </div>
                        
                        {pricingConfig.hourly_enabled && (
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">R$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={pricingConfig.hourly_rate}
                              onChange={(e) => setPricingConfig({...pricingConfig, hourly_rate: parseFloat(e.target.value) || 0})}
                              className="w-32"
                              placeholder="0,00"
                            />
                            <span className="text-sm text-gray-500">Valor padrão por hora</span>
                          </div>
                        )}
                      </div>

                      {/* Seção: Hora Adicional com Desconto */}
                      <div className="mb-6 p-4 border-2 border-dashed border-orange-300 rounded-lg bg-orange-50/30">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">Hora Adicional com Desconto</h4>
                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded">NOVO</span>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={pricingConfig.progressive_enabled}
                              onChange={(e) => setPricingConfig({...pricingConfig, progressive_enabled: e.target.checked})}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                          </label>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">1ª hora valor cheio, demais com desconto</p>
                        
                        {pricingConfig.progressive_enabled && (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-1">1ª Hora</label>
                              <div className="flex items-center space-x-2">
                                <span className="text-gray-600">R$</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={pricingConfig.first_hour_rate}
                                  onChange={(e) => setPricingConfig({...pricingConfig, first_hour_rate: parseFloat(e.target.value) || 0})}
                                  className="flex-1"
                                  placeholder="15,00"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Hora adicional</label>
                              <div className="flex items-center space-x-2">
                                <span className="text-gray-600">R$</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={pricingConfig.additional_hour_rate}
                                  onChange={(e) => setPricingConfig({...pricingConfig, additional_hour_rate: parseFloat(e.target.value) || 0})}
                                  className="flex-1"
                                  placeholder="8,00"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Seção: Preços Especiais */}
                      <div className="p-4 border-2 border-dashed border-orange-300 rounded-lg bg-orange-50/30">
                        <div className="flex items-center mb-4">
                          <h4 className="font-semibold text-gray-900">Preços Especiais</h4>
                          <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded">NOVO</span>
                        </div>

                        <div className="space-y-4">
                          {/* Período */}
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div className="flex items-center space-x-3">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={pricingConfig.period_enabled}
                                  onChange={(e) => setPricingConfig({...pricingConfig, period_enabled: e.target.checked})}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                              </label>
                              <div>
                                <p className="font-medium">Período</p>
                                <p className="text-xs text-gray-500">Consulte a administração local</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-600">R$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pricingConfig.period_rate}
                                onChange={(e) => setPricingConfig({...pricingConfig, period_rate: parseFloat(e.target.value) || 0})}
                                className="w-24"
                                placeholder="35,00"
                                disabled={!pricingConfig.period_enabled}
                              />
                            </div>
                          </div>

                          {/* Diária Completa */}
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div className="flex items-center space-x-3">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={pricingConfig.daily_enabled}
                                  onChange={(e) => setPricingConfig({...pricingConfig, daily_enabled: e.target.checked})}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                              </label>
                              <div>
                                <p className="font-medium">Diária Completa</p>
                                <p className="text-xs text-gray-500">Valor fixo para 24 horas</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-600">R$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pricingConfig.daily_rate}
                                onChange={(e) => setPricingConfig({...pricingConfig, daily_rate: parseFloat(e.target.value) || 0})}
                                className="w-24"
                                placeholder="50,00"
                                disabled={!pricingConfig.daily_enabled}
                              />
                            </div>
                          </div>

                          {/* Turno Manhã */}
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div className="flex items-center space-x-3">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={pricingConfig.morning_shift_enabled}
                                  onChange={(e) => setPricingConfig({...pricingConfig, morning_shift_enabled: e.target.checked})}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                              </label>
                              <div>
                                <p className="font-medium">Turno Manhã (6h-12h)</p>
                                <p className="text-xs text-gray-500">6 horas no período da manhã</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-600">R$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pricingConfig.morning_shift_rate}
                                onChange={(e) => setPricingConfig({...pricingConfig, morning_shift_rate: parseFloat(e.target.value) || 0})}
                                className="w-24"
                                placeholder="30,00"
                                disabled={!pricingConfig.morning_shift_enabled}
                              />
                            </div>
                          </div>

                          {/* Turno Tarde */}
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div className="flex items-center space-x-3">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={pricingConfig.afternoon_shift_enabled}
                                  onChange={(e) => setPricingConfig({...pricingConfig, afternoon_shift_enabled: e.target.checked})}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                              </label>
                              <div>
                                <p className="font-medium">Turno Tarde (12h-18h)</p>
                                <p className="text-xs text-gray-500">6 horas no período da tarde</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-600">R$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pricingConfig.afternoon_shift_rate}
                                onChange={(e) => setPricingConfig({...pricingConfig, afternoon_shift_rate: parseFloat(e.target.value) || 0})}
                                className="w-24"
                                placeholder="30,00"
                                disabled={!pricingConfig.afternoon_shift_enabled}
                              />
                            </div>
                          </div>

                          {/* Turno Noite */}
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div className="flex items-center space-x-3">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={pricingConfig.night_shift_enabled}
                                  onChange={(e) => setPricingConfig({...pricingConfig, night_shift_enabled: e.target.checked})}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                              </label>
                              <div>
                                <p className="font-medium">Turno Noite (18h-6h)</p>
                                <p className="text-xs text-gray-500">12 horas no período noturno</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-600">R$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pricingConfig.night_shift_rate}
                                onChange={(e) => setPricingConfig({...pricingConfig, night_shift_rate: parseFloat(e.target.value) || 0})}
                                className="w-24"
                                placeholder="25,00"
                                disabled={!pricingConfig.night_shift_enabled}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Dica */}
                      <div className="mt-6 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                        <p className="text-sm text-yellow-800">
                          <strong>💡 Dica:</strong> Ative apenas as opções que fazem sentido para seu estacionamento. 
                          O motorista verá todas as opções ativas e escolherá a melhor para ele.
                        </p>
                      </div>

                      {/* Botão Salvar */}
                      <div className="mt-6">
                        <Button
                          onClick={savePricingConfig}
                          disabled={savingPricing}
                          style={{ backgroundColor: '#302681' }}
                          className="w-full text-white py-3 text-lg font-semibold"
                        >
                          {savingPricing ? 'Salvando...' : 'Salvar Configuração de Preços'}
                        </Button>
                      </div>
                    </Card>
                  ) : (
                    <Card className="p-12 text-center">
                      <h3 className="text-xl font-semibold mb-2">Selecione um Estacionamento</h3>
                      <p className="text-gray-600">Escolha um estacionamento na lista ao lado para configurar os preços</p>
                    </Card>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Finances Tab Content */}
        {activeTab === 'finances' && (
          <div className="space-y-6">
            {/* Earnings Summary */}
            {earnings && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Receita Total</h3>
                  <p className="text-3xl font-bold text-blue-700">
                    R$ {earnings.total_revenue.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{earnings.total_transactions} transações</p>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Seus Ganhos</h3>
                  <p className="text-3xl font-bold text-green-700">
                    R$ {earnings.owner_earnings.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    ({(100 - earnings.commission_percentage).toFixed(0)}% da receita)
                  </p>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-amber-50 to-amber-100">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Aguardando Pagamento</h3>
                  <p className="text-3xl font-bold text-amber-700">
                    R$ {earnings.pending_payout.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Pago: R$ {earnings.paid_out.toFixed(2)}</p>
                </Card>
              </div>
            )}

            {/* Bank Account Form */}
            <Card className="p-6">
              <h2 className="text-xl font-bold mb-4">Dados Bancários para Recebimento</h2>
              <p className="text-sm text-gray-600 mb-6">
                Cadastre seus dados bancários para receber os pagamentos dos seus estacionamentos
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nome do Banco</label>
                  <Input
                    value={bankAccount.bank_name}
                    onChange={(e) => setBankAccount({...bankAccount, bank_name: e.target.value})}
                    placeholder="Ex: Banco do Brasil"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Tipo de Conta</label>
                  <select
                    value={bankAccount.account_type}
                    onChange={(e) => setBankAccount({...bankAccount, account_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="checking">Conta Corrente</option>
                    <option value="savings">Conta Poupança</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Agência</label>
                  <Input
                    value={bankAccount.agency}
                    onChange={(e) => setBankAccount({...bankAccount, agency: e.target.value})}
                    placeholder="Ex: 1234"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Número da Conta</label>
                  <Input
                    value={bankAccount.account_number}
                    onChange={(e) => setBankAccount({...bankAccount, account_number: e.target.value})}
                    placeholder="Ex: 12345-6"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Titular da Conta</label>
                  <Input
                    value={bankAccount.account_holder_name}
                    onChange={(e) => setBankAccount({...bankAccount, account_holder_name: e.target.value})}
                    placeholder="Nome completo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">CPF/CNPJ</label>
                  <Input
                    value={bankAccount.cpf_cnpj}
                    onChange={(e) => setBankAccount({...bankAccount, cpf_cnpj: e.target.value})}
                    placeholder="000.000.000-00"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Chave PIX (Opcional)</label>
                  <Input
                    value={bankAccount.pix_key}
                    onChange={(e) => setBankAccount({...bankAccount, pix_key: e.target.value})}
                    placeholder="Email, telefone, CPF ou chave aleatória"
                  />
                </div>
              </div>

              <div className="mt-6">
                <Button
                  onClick={saveBankAccount}
                  style={{ backgroundColor: '#302681' }}
                  className="text-white"
                >
                  Salvar Dados Bancários
                </Button>
              </div>
            </Card>

            {/* Transaction History */}
            {earnings && earnings.payments && earnings.payments.length > 0 && (
              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4">Últimas Transações</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Data</th>
                        <th className="text-left py-3 px-4">Estacionamento</th>
                        <th className="text-left py-3 px-4">Horas</th>
                        <th className="text-right py-3 px-4">Valor</th>
                        <th className="text-center py-3 px-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {earnings.payments.map((payment) => (
                        <tr key={payment.id} className="border-b">
                          <td className="py-3 px-4">
                            {new Date(payment.created_at).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="py-3 px-4">{payment.parking_lot_id}</td>
                          <td className="py-3 px-4">{payment.hours}h</td>
                          <td className="text-right py-3 px-4 font-semibold">
                            R$ {payment.amount.toFixed(2)}
                          </td>
                          <td className="text-center py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              payment.status === 'approved' ? 'bg-green-100 text-green-800' :
                              payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {payment.status === 'approved' ? 'Aprovado' :
                               payment.status === 'pending' ? 'Pendente' : 'Rejeitado'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Mercado Pago Tab Content */}
        {activeTab === 'mercadopago' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">💰 Integração com Mercado Pago</h2>

            {mpStatus.loading ? (
              <Card className="p-6 text-center">
                <p className="text-gray-600">Carregando...</p>
              </Card>
            ) : !mpStatus.connected ? (
              // NOT CONNECTED
              <Card className="p-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-300">
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="text-4xl">⚠️</div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Conta Mercado Pago não conectada
                      </h3>
                      <p className="text-gray-700 mb-4">
                        Conecte sua conta Mercado Pago para receber pagamentos automaticamente!
                      </p>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg space-y-2">
                    <p className="font-semibold text-gray-900">✨ Vantagens:</p>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• <strong>Receba 88% dos pagamentos</strong> DIRETO na sua conta</li>
                      <li>• Sem transferências manuais</li>
                      <li>• Dinheiro cai em tempo real</li>
                      <li>• Total transparência</li>
                    </ul>
                  </div>

                  {mpAuthUrl ? (
                    <a
                      href={mpAuthUrl}
                      className="w-full py-6 text-lg font-bold text-white flex items-center justify-center rounded-md hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: '#3483FA', textDecoration: 'none' }}
                    >
                      🔗 Conectar Mercado Pago
                    </a>
                  ) : authUrlError ? (
                    <div className="text-center">
                      <p className="text-red-500 mb-2">Erro ao carregar link de conexão.</p>
                      <Button
                        onClick={fetchMpAuthUrl}
                        className="w-full py-6 text-lg font-bold text-white"
                        style={{ backgroundColor: '#FF5A5F' }}
                      >
                        🔄 Tentar Novamente
                      </Button>
                    </div>
                  ) : (
                    <Button
                      disabled
                      className="w-full py-6 text-lg font-bold text-white"
                      style={{ backgroundColor: '#3483FA', opacity: 0.7 }}
                    >
                      🔄 Carregando link...
                    </Button>
                  )}

                  <div className="bg-blue-50 p-3 rounded border-l-4 border-blue-400">
                    <p className="text-sm text-blue-800">
                      <strong>ℹ️ Como funciona?</strong><br/>
                      Cliente paga R$ 100 → Você recebe R$ 88 direto na sua conta MP<br/>
                      Taxa Mercado Pago: ~1-5% | Comissão ParkingSpot: ~7-11%
                    </p>
                  </div>
                </div>
              </Card>
            ) : (
              // CONNECTED
              <>
                <Card className="p-6 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-400">
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className="text-4xl">✅</div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            Conta Mercado Pago Conectada
                          </h3>
                          <p className="text-gray-700 mb-3">
                            🎉 Parabéns! Sua conta está conectada
                          </p>
                          <div className="space-y-1 text-sm text-gray-700">
                            <p>👤 <strong>Usuário MP:</strong> {mpStatus.mp_user_id}</p>
                            {mpStatus.connected_at && (
                              <p>📅 <strong>Conectado em:</strong> {new Date(mpStatus.connected_at).toLocaleString('pt-BR')}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={disconnectMercadoPago}
                        variant="destructive"
                        size="sm"
                      >
                        🔌 Desconectar
                      </Button>
                    </div>

                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-700">
                        💵 <strong>Você recebe:</strong> 88% dos pagamentos direto na sua conta<br/>
                        🏦 <strong>Próximos pagamentos:</strong> Já usam split automático
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4">📊 Como Funciona o Split Payment</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="bg-white p-3 rounded shadow-sm">
                        <p className="text-xs text-gray-600 mb-1">Total Pago</p>
                        <p className="text-xl font-bold text-gray-900">R$ 100</p>
                      </div>
                      <div className="bg-green-100 p-3 rounded shadow-sm">
                        <p className="text-xs text-gray-600 mb-1">Você Recebe</p>
                        <p className="text-xl font-bold text-green-700">R$ 88</p>
                        <p className="text-xs text-green-600">(88%)</p>
                      </div>
                      <div className="bg-blue-100 p-3 rounded shadow-sm">
                        <p className="text-xs text-gray-600 mb-1">Plataforma</p>
                        <p className="text-xl font-bold text-blue-700">~R$ 7-11</p>
                        <p className="text-xs text-blue-600">(~7-11%)</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 text-center">
                      * Valores aproximados. A comissão da plataforma varia conforme a taxa do Mercado Pago (PIX ~1%, Cartão ~5%)
                    </p>
                  </div>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Analytics Tab Content */}
        {activeTab === 'analytics' && (
          <AnalyticsDashboard authToken={authToken} />
        )}
      </main>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingLot ? 'Editar Estacionamento' : 'Novo Estacionamento'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Nome</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="form-name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Endereço</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                required
                data-testid="form-address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Descrição <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => {
                  if (e.target.value.length <= 150) {
                    setFormData({...formData, description: e.target.value});
                  }
                }}
                placeholder="Ex: Estacionamento seguro com câmeras 24h, manobrista disponível..."
                className="w-full p-2 border rounded-lg resize-none focus:ring-2 focus:ring-[#302681] focus:border-transparent"
                rows={2}
                maxLength={150}
                data-testid="form-description"
              />
              <p className="text-xs text-gray-500 mt-1 text-right">
                {formData.description?.length || 0}/150 caracteres
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Latitude</label>
                <Input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({...formData, latitude: parseFloat(e.target.value)})}
                  required
                  data-testid="form-latitude"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Longitude</label>
                <Input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({...formData, longitude: parseFloat(e.target.value)})}
                  required
                  data-testid="form-longitude"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Total de Vagas</label>
                <Input
                  type="number"
                  min="0"
                  value={formData.total_spots}
                  onChange={(e) => setFormData({...formData, total_spots: parseInt(e.target.value)})}
                  required
                  data-testid="form-total-spots"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Vagas Disponíveis</label>
                <Input
                  type="number"
                  min="0"
                  value={formData.available_spots}
                  onChange={(e) => setFormData({...formData, available_spots: parseInt(e.target.value)})}
                  required
                  data-testid="form-available-spots"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Preço por Hora (R$)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.price_per_hour}
                onChange={(e) => setFormData({...formData, price_per_hour: parseFloat(e.target.value)})}
                required
                data-testid="form-price"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Tipo</label>
              <select
                value={formData.parking_type}
                onChange={(e) => setFormData({...formData, parking_type: e.target.value})}
                className="w-full p-2 border rounded-lg"
                data-testid="form-type"
              >
                <option value="indoor">Coberto (Indoor)</option>
                <option value="outdoor">Descoberto (Outdoor)</option>
                <option value="mixed">Misto</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Horário de Funcionamento</label>
              <Input
                value={formData.schedule}
                onChange={(e) => setFormData({...formData, schedule: e.target.value})}
                placeholder="Ex: 24 horas, Segunda a Sexta 8h-18h"
                data-testid="form-schedule"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Fotos do Estacionamento (máx {MAX_PHOTOS} fotos, 5 MB total)
              </label>
              
              {/* Grid de fotos existentes e previews */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {/* Fotos existentes (já salvas) */}
                {formData.photo_urls?.map((url, idx) => (
                  <div key={`existing-${idx}`} className="relative aspect-square">
                    <img 
                      src={url} 
                      alt={`Foto ${idx + 1}`} 
                      className="w-full h-full object-cover rounded-lg border-2 border-green-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeExistingPhoto(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                      title="Remover foto"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <span className="absolute bottom-1 left-1 bg-green-500 text-white text-xs px-1 rounded">
                      Salva
                    </span>
                  </div>
                ))}
                
                {/* Previews de novas fotos (não salvas ainda) */}
                {photoPreviews
                  .filter(p => !formData.photo_urls?.includes(p))
                  .map((preview, idx) => (
                  <div key={`preview-${idx}`} className="relative aspect-square">
                    <img 
                      src={preview} 
                      alt={`Nova foto ${idx + 1}`} 
                      className="w-full h-full object-cover rounded-lg border-2 border-orange-400"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(formData.photo_urls?.length + idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                      title="Remover foto"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <span className="absolute bottom-1 left-1 bg-orange-400 text-white text-xs px-1 rounded">
                      Nova
                    </span>
                  </div>
                ))}
                
                {/* Slot para adicionar mais fotos */}
                {((formData.photo_urls?.length || 0) + selectedFiles.length) < MAX_PHOTOS && (
                  <div className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-[#302681] transition cursor-pointer">
                    <input
                      type="file"
                      id="photo-upload"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                      multiple
                      data-testid="photo-input"
                    />
                    <label 
                      htmlFor="photo-upload" 
                      className="cursor-pointer flex flex-col items-center p-4"
                    >
                      <Upload className="w-8 h-8 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-600 text-center">
                        Adicionar foto
                      </span>
                    </label>
                  </div>
                )}
              </div>
              
              {/* Info sobre fotos */}
              <p className="text-xs text-gray-500">
                {(formData.photo_urls?.length || 0) + selectedFiles.length}/{MAX_PHOTOS} fotos • JPEG, PNG ou WebP • Máx 2 MB por foto
              </p>
            </div>

            <div className="flex space-x-4 pt-4">
              <Button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingLot(null);
                  resetForm();
                }}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                style={{ backgroundColor: '#302681' }}
                className="flex-1 text-white"
                data-testid="form-submit"
              >
                {editingLot ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OwnerDashboard;
